import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Download, Calendar, Euro, ChevronRight, 
  Receipt, CreditCard, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchInvoices();
    }
  }, [user, authLoading, navigate]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Kon facturen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    if (invoice.pdf_url) {
      window.open(invoice.pdf_url, '_blank');
    } else {
      // Generate PDF on demand
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `factuur-${invoice.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Factuur gedownload');
      } catch (error) {
        toast.error('Download mislukt');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20 max-w-4xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Financiën</span>
          </div>
          <h1 className="text-4xl font-medium tracking-tight mb-3">
            Mijn <span className="brand-gold text-5xl">Facturen</span>
          </h1>
          <p className="text-gray-400">Bekijk en download je aankoopbewijzen</p>
        </div>

        {/* Invoice List */}
        {invoices.length === 0 ? (
          <div className="text-center py-16 glass rounded-xl">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">Geen facturen</h3>
            <p className="text-gray-400 mb-6">Je hebt nog geen aankopen gedaan</p>
            <Button 
              onClick={() => navigate('/packages')}
              className="btn-gold px-8"
            >
              Bekijk Pakketten
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice, index) => (
              <div 
                key={invoice.id || index}
                className="glass rounded-xl p-6 border border-white/5 hover:border-[#D4AF37]/30 transition-all"
                data-testid={`invoice-${index}`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">
                        Factuur #{invoice.id?.slice(0, 8) || index + 1}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(invoice.date || invoice.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          {invoice.payment_method || 'Stripe'}
                        </span>
                      </div>
                      {invoice.items && invoice.items.length > 0 && (
                        <p className="text-sm text-gray-500 mt-2">
                          {invoice.items.map(i => i.name || i.description).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#D4AF37]">
                        €{(invoice.total || invoice.amount || 0).toFixed(2)}
                      </p>
                      {invoice.vat && (
                        <p className="text-xs text-gray-500">
                          incl. €{invoice.vat.toFixed(2)} BTW
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => downloadInvoice(invoice)}
                      variant="outline"
                      className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      data-testid={`download-invoice-${index}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {invoices.length > 0 && (
          <div className="mt-8 p-6 glass rounded-xl border border-[#D4AF37]/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Totaal uitgegeven</p>
                <p className="text-3xl font-bold text-[#D4AF37]">
                  €{invoices.reduce((acc, inv) => acc + (inv.total || inv.amount || 0), 0).toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Aantal facturen</p>
                <p className="text-3xl font-bold text-white">{invoices.length}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
